/**
 * Pipeline & Facility quotes (PF-5c, fieldiq-api#1232) on the shared quote
 * screens: the rate card as a pricing source, "Create Pipeline job" on an
 * accepted Pipeline quote, and every refusal those doors answer with as a
 * sentence.
 *
 * Every helper answers for a Pipeline item, line or quote only and returns null
 * for anything else, so the shared screens keep exactly what they rendered
 * before for casing, rentals, downhole, pressure testing and NDT.
 *
 * THE API DECIDES. Nothing here prices a line, picks a winning rate, or decides
 * which items may be quoted: an item's `refusal` is the API's (a rate tied with
 * another in the ticket's winning tier), and the screen only declines to offer it.
 */
import { pfApiError } from './pipeline-facility';

export const PF_QUOTE_VERTICAL = 'pipeline_facility';
export const PF_QUOTE_SOURCE_KIND = 'pf_rate';

/**
 * The terms beside a rate-card item, never priced: its code, kind and what it
 * applies to (a crew role, a unit, a type), and the card's notes. Null for an
 * item from any other source, whose terms the screen spells its own way.
 */
export function pfQuoteItemTerms(item: { sourceKind?: string | null; terms?: Record<string, unknown> | null }): string | null {
  if (item.sourceKind !== PF_QUOTE_SOURCE_KIND) return null;
  const t = item.terms ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const parts = [str(t.code), str(t.kindLabel), str(t.appliesTo), str(t.notes)].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The currency a Pipeline quote's money is spelled in: the quote row's own
 * code (stamped from companies.currency at create). Null for any other
 * vertical, which keeps fmtCurrency's bare "$" there, unchanged.
 */
export function pfQuoteCurrency(quote: { vertical?: string | null; currency?: string | null } | null | undefined): string | null {
  if (!quote || quote.vertical !== PF_QUOTE_VERTICAL) return null;
  return quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
}

/**
 * The Type column for a line priced from the Pipeline Rate Card (source_kind
 * 'pf_rate'). The API writes every such line as line_item_type 'other', so the
 * kind is read from the unit it stamps (api pfQuoteUnit). Null for any other
 * line; a Pipeline line with an unknown unit is `—`, never "Other".
 */
const PF_QUOTE_UNIT_KIND: Record<string, string> = {
  per_hour: 'Per hour',
  per_day: 'Per day',
  per_km: 'Per distance',
  per_mile: 'Per distance',
  flat_fee: 'Flat charge',
  each: 'Each',
};
export function pfQuoteLineKind(line: { source_kind?: string | null; unit?: string | null }): string | null {
  if (line.source_kind !== PF_QUOTE_SOURCE_KIND) return null;
  return (line.unit && PF_QUOTE_UNIT_KIND[line.unit]) || '—';
}

/**
 * Every refusal the two Pipeline quote doors answer with. `api: true` means the
 * API's own words win when it sent some, because they carry the specifics
 * ("Line 2 (Welder — per hour): …"); the sentence here is what is said when
 * they are absent. `api: false` codes answer in words written for a developer
 * ("site_id is a site of a different customer"), so this sentence is always used.
 */
const PF_QUOTE_REFUSALS: Record<string, { api: boolean; sentence: string }> = {
  // POST /quotes/from-pricing-source
  QUOTE_RATE_AMBIGUOUS: { api: true, sentence: 'More than one rate on the Pipeline Rate Card applies to this line, so it cannot be quoted. Fix the rate card, then quote it.' },
  QUOTE_ITEM_NOT_AVAILABLE: { api: true, sentence: 'One of the picked rates no longer applies to this customer today. Go back and pick again.' },
  QUOTE_CURRENCY_MISMATCH: { api: true, sentence: "One of the picked rates is in a different currency from the company's, so it cannot be quoted." },
  QUOTE_QUANTITY_INVALID: { api: true, sentence: 'Enter a quantity greater than zero on every picked line.' },
  QUOTE_LINE_UNPRICED: { api: true, sentence: 'One of the picked lines has no rate on the Pipeline Rate Card, so it cannot be quoted.' },
  VERTICAL_NOT_ENABLED: { api: false, sentence: 'Pipeline & Facility is not turned on for this company, so it cannot be quoted.' },
  // POST /quotes/:id/create-pf-job
  QUOTE_NOT_FOUND: { api: false, sentence: 'This quote was not found. It may have been deleted.' },
  QUOTE_NOT_PIPELINE: { api: true, sentence: 'Only a Pipeline quote becomes a Pipeline job.' },
  QUOTE_NOT_ACCEPTED: { api: true, sentence: 'Accept the quote before creating its Pipeline job.' },
  QUOTE_CUSTOMER_REQUIRED: { api: true, sentence: 'The quote has no customer to create a Pipeline job for.' },
  SITE_REQUIRED: { api: false, sentence: "Pick the site this job is on. A Pipeline job always belongs to one of the customer's sites." },
  site_customer_mismatch: { api: false, sentence: "That site belongs to a different customer. Pick one of this quote's customer's sites." },
  QUOTE_JOB_ALREADY_CREATED: { api: true, sentence: "This quote's Pipeline job was just created. Refresh to see it." },
  SCHEDULED_DATE_INVALID: { api: false, sentence: 'The job day is not a valid date. Pick it again or leave it blank.' },
};

export const PF_QUOTE_REFUSAL_CODES = Object.keys(PF_QUOTE_REFUSALS);

/**
 * A refused Pipeline quote write as `{ code, error }` — the shape the quote
 * screens' refusal notice draws. A 403 is a refusal of permission whatever the
 * body says (the create-job door is admin / office staff), never a slip.
 */
export function pfQuoteRefusal(err: unknown, fallback: string): { code?: string; error: string } {
  const e = pfApiError(err);
  const code = e.code ?? undefined;
  if (e.status === 403 && code !== 'VERTICAL_NOT_ENABLED') {
    return { code, error: 'You do not have permission to do this. Ask an admin or office staff.' };
  }
  const known = code ? PF_QUOTE_REFUSALS[code] : undefined;
  if (known) return { code, error: known.api && e.message ? e.message : known.sentence };
  return { code, error: e.message ?? fallback };
}
